import React from 'react';

/**
 * Smoke Background Effect
 * Thin flowing lines with combined motion:
 * - Drift (X/Y), Scale, and Opacity all animated together
 * - Continuous leftward drift that never stops
 *
 * Three depth layers with parallax:
 * - Back: Slowest, highest blur (80px), lowest opacity
 * - Middle: Medium speed, medium blur (50px)
 * - Front: Fastest, lowest blur (30px), higher contrast
 */

const MistGradient = ({ className = '' }) => {
  return (
    <div
      className={`absolute inset-0 overflow-hidden ${className}`}
      style={{
        animation: 'mistFadeIn 1.4s ease-out 0.3s both',
      }}
    >
      {/* Back Layer - Slowest, highest blur, lowest opacity */}
      <div
        className="absolute"
        style={{
          top: '-30%',
          left: '-50%',
          width: '200%',
          height: '160%',
          filter: 'blur(80px)',
          animation: 'smokeDrift1 6s ease-in-out infinite alternate',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '-10%',
            width: '120%',
            height: '180px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 80%, transparent 100%)',
            transform: 'rotate(-15deg)',
            borderRadius: '100px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '20%',
            left: '-5%',
            width: '110%',
            height: '150px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.25) 60%, rgba(255,255,255,0.1) 85%, transparent 100%)',
            transform: 'rotate(8deg)',
            borderRadius: '100px',
          }}
        />
      </div>

      {/* Middle Layer - Medium speed, medium blur */}
      <div
        className="absolute"
        style={{
          top: '-30%',
          left: '-50%',
          width: '200%',
          height: '160%',
          filter: 'blur(50px)',
          animation: 'smokeDrift2 5s ease-in-out infinite alternate',
          animationDelay: '-1.5s',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '25%',
            left: '-15%',
            width: '130%',
            height: '120px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 15%, rgba(255,255,255,0.5) 45%, rgba(255,255,255,0.5) 55%, rgba(255,255,255,0.2) 85%, transparent 100%)',
            transform: 'rotate(-25deg)',
            borderRadius: '80px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '-10%',
            width: '120%',
            height: '100px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.15) 80%, transparent 100%)',
            transform: 'rotate(-18deg)',
            borderRadius: '60px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '15%',
            right: '-10%',
            width: '80%',
            height: '90px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 25%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.2) 75%, transparent 100%)',
            transform: 'rotate(12deg)',
            borderRadius: '60px',
          }}
        />
      </div>

      {/* Front Layer - Fastest, lowest blur, higher contrast */}
      <div
        className="absolute"
        style={{
          top: '-30%',
          left: '-50%',
          width: '200%',
          height: '160%',
          filter: 'blur(30px)',
          animation: 'smokeDrift3 4s ease-in-out infinite alternate',
          animationDelay: '-0.5s',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '30%',
            left: '-20%',
            width: '140%',
            height: '80px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 10%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.6) 60%, rgba(255,255,255,0.25) 90%, transparent 100%)',
            transform: 'rotate(-22deg)',
            borderRadius: '50px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '55%',
            left: '-5%',
            width: '100%',
            height: '60px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.2) 80%, transparent 100%)',
            transform: 'rotate(-12deg)',
            borderRadius: '40px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '-5%',
            width: '70%',
            height: '70px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 30%, rgba(255,255,255,0.4) 60%, rgba(255,255,255,0.15) 90%, transparent 100%)',
            transform: 'rotate(15deg)',
            borderRadius: '40px',
          }}
        />
      </div>

      {/* Grain/Noise Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.06,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      <style>{`
        @keyframes mistFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        /* Layer 1 - Wide horizontal drift */
        @keyframes smokeDrift1 {
          0% {
            transform: translateX(400px) scale(1.02);
          }
          100% {
            transform: translateX(-400px) scale(1.05);
          }
        }

        /* Layer 2 - Wide horizontal drift (offset) */
        @keyframes smokeDrift2 {
          0% {
            transform: translateX(350px) scale(1.03);
          }
          100% {
            transform: translateX(-350px) scale(1.06);
          }
        }

        /* Layer 3 - Wide horizontal drift (fastest) */
        @keyframes smokeDrift3 {
          0% {
            transform: translateX(300px) scale(1.02);
          }
          100% {
            transform: translateX(-300px) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          [style*="smokeDrift"] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default MistGradient;
